import { useEffect, useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { usePaymentContext } from 'features/payment';
import { IPaymentMethod } from 'features/payment/types';
import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { ServiceMode } from 'generated/graphql-gateway';
import { primitive } from 'styles/constants/primitives';

import { useCheckoutLoadingState } from '../../../checkout/hooks';

import { SelectorAvailablePayments } from './selector-available-payments';
import { SelectorBottomSheet } from './selector-bottom-sheet';
import { SelectorLoading } from './selector-loading';
import { SelectorPrepaidReload } from './selector-prepaid-reload';
import { SelectorSelection } from './selector-selection';
import { SelectionType, SelectorProps } from './types';

export type PaymentMethodSelectorProps = SelectorProps & {
  isLoading?: boolean;
  noPaymentMethodsAvailable?: boolean;
  serviceMode: ServiceMode;
  prepaid?: {
    allowedMethodsForReload: IPaymentMethod[];
    shouldReloadPrepaidMethod: boolean;
    reloadAmount: number;
    selectedMethodId: string | undefined;
  };
  onSelect: (type: SelectionType, id?: string) => void;
};
export function PaymentMethodSelector({
  methods,
  selectedMethodId,
  isLoading,
  noPaymentMethodsAvailable,
  serviceMode,
  prepaid,
  onSelect,
}: PaymentMethodSelectorProps) {
  const { formatMessage } = useIntl();
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [isPrepaidReload, setIsPrepaidReload] = useState(false);
  const { clearPaymentDetails } = usePaymentContext();
  const { isCheckoutLoading } = useCheckoutLoadingState();

  const {
    shouldReloadPrepaidMethod = false,
    reloadAmount = 0,
    selectedMethodId: selectedReloadPaymentMethodId,
    allowedMethodsForReload: reloadPaymentMethods = [],
  } = prepaid || {};

  useEffect(() => {
    clearPaymentDetails();

    return () => {
      // For curbside, we need to preserve this data to continue with the "I'm Here" flow
      if (serviceMode !== ServiceMode.CURBSIDE) {
        clearPaymentDetails();
      }
    };
  }, [clearPaymentDetails, serviceMode]);

  const handleCloseSelector = () => {
    setIsSelectorOpen(false);
    // allows the dialog to dismiss before changing the content back to the default selector
    setTimeout(() => {
      setIsPrepaidReload(false);
    }, 100);
  };

  const handleSelection = (type: SelectionType, id?: string) => {
    onSelect(type, isPrepaidReload ? `prepaid-${id}` : id);
    handleCloseSelector();
  };

  if (isLoading) {
    return (
      <View style={styles.selectorContainer}>
        <SelectorLoading />
      </View>
    );
  }

  if (noPaymentMethodsAvailable) {
    return (
      <View style={styles.selectorContainer}>
        <ActionButton
          testID="add-payment-btn"
          width="100%"
          height="$10"
          marginY="$6"
          _text={{ fontSize: 'sm' }}
          variant={ActionButtonVariants.OUTLINE}
          leftIcon={<Icon variant="add" aria-hidden />}
          onPress={() => onSelect('add')}
        >
          {formatMessage({ id: 'addPayment' })}
        </ActionButton>
      </View>
    );
  }

  return (
    <View style={WidgetBaseStyles.container}>
      <View style={styles.selectorContainer}>
        <SelectorSelection
          methods={methods}
          selectedMethodId={selectedMethodId}
          onChange={() => setIsSelectorOpen(true)}
          isDisabled={isCheckoutLoading}
        />
        {shouldReloadPrepaidMethod && (
          <SelectorPrepaidReload
            methods={reloadPaymentMethods}
            selectedMethodId={selectedReloadPaymentMethodId}
            reloadAmount={reloadAmount}
            onChange={() => {
              setIsSelectorOpen(true);
              setIsPrepaidReload(true);
            }}
          />
        )}

        {/* This display the bottom banner with the available methods as icons */}
        <View style={styles.bottomSection}>
          <SelectorAvailablePayments
            paymentMethods={methods}
            isReloadPrepaidBeingShown={shouldReloadPrepaidMethod}
          />
        </View>
      </View>
      <SelectorBottomSheet
        isOpen={isSelectorOpen}
        isPrepaidReload={isPrepaidReload}
        methods={isPrepaidReload ? reloadPaymentMethods : methods}
        selectedMethodId={isPrepaidReload ? selectedReloadPaymentMethodId : selectedMethodId}
        noPaymentMethodsAvailable={noPaymentMethodsAvailable}
        onClose={handleCloseSelector}
        onSelect={handleSelection}
      />
    </View>
  );
}

export const WidgetBaseStyles = StyleSheet.create({
  container: {
    backgroundColor: primitive.$white,
    borderRadius: 10,
    overflow: 'hidden',
    padding: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});

const styles = StyleSheet.create({
  selectorContainer: {
    minHeight: 112,
    backgroundColor: primitive.$white,
    justifyContent: 'space-between',
    flex: 1,
  },
  bottomSection: {
    display: 'flex',
    flexDirection: 'row',
    height: 32,
    width: '100%',
    gap: primitive.spacing.$2,
    backgroundColor: primitive.bk.$lightBrown,
    borderBottomEndRadius: 10,
    borderBottomStartRadius: 10,
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: primitive.spacing.$4,
  },
});
